package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.speedcamera.SpeedCameraType
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.close
import com.flowyride.feature.planner.resources.locate_me
import com.flowyride.feature.planner.resources.speed_camera_limit
import com.flowyride.feature.planner.resources.speed_camera_limit_unknown
import com.flowyride.feature.planner.resources.speed_camera_type_discriminating
import com.flowyride.feature.planner.resources.speed_camera_type_fixed
import com.flowyride.feature.planner.resources.speed_camera_type_level_crossing
import com.flowyride.feature.planner.resources.speed_camera_type_red_light
import com.flowyride.feature.planner.resources.speed_camera_type_section
import com.flowyride.feature.planner.resources.speed_camera_type_tower
import com.flowyride.feature.planner.resources.speed_camera_type_unknown
import com.flowyride.feature.planner.resources.speed_camera_type_urban
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** Round "centre on my position" button; the target is drawn (no icon font on the web). */
@Composable
internal fun LocateButton(isLocating: Boolean, onClick: () -> Unit, modifier: Modifier = Modifier) {
    val description = stringResource(Res.string.locate_me)
    Surface(
        onClick = onClick,
        enabled = !isLocating,
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 6.dp,
        modifier = modifier.size(48.dp).semantics { contentDescription = description },
    ) {
        Box(contentAlignment = Alignment.Center) {
            if (isLocating) {
                CircularProgressIndicator(Modifier.size(22.dp), strokeWidth = 2.dp)
            } else {
                val color = MaterialTheme.colorScheme.primary
                Canvas(Modifier.size(24.dp)) {
                    val r = size.minDimension / 2
                    val stroke = 2.dp.toPx()
                    drawCircle(color, radius = r * 0.62f, style = Stroke(stroke))
                    drawCircle(color, radius = r * 0.25f)
                    val c = center
                    drawLine(color, Offset(c.x, 0f), Offset(c.x, r * 0.38f), stroke)
                    drawLine(color, Offset(c.x, size.height - r * 0.38f), Offset(c.x, size.height), stroke)
                    drawLine(color, Offset(0f, c.y), Offset(r * 0.38f, c.y), stroke)
                    drawLine(color, Offset(size.width - r * 0.38f, c.y), Offset(size.width, c.y), stroke)
                }
            }
        }
    }
}

/** What a tapped camera checks, and how fast one may go there. */
@Composable
internal fun SpeedCameraDialog(camera: SpeedCamera, onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(camera.type.label)) },
        text = {
            Text(
                camera.speedLimitKmh?.let { stringResource(Res.string.speed_camera_limit, it) }
                    ?: stringResource(Res.string.speed_camera_limit_unknown),
                style = MaterialTheme.typography.titleMedium,
            )
        },
        confirmButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.close)) } },
    )
}

private val SpeedCameraType.label: StringResource
    get() = when (this) {
        SpeedCameraType.FIXED -> Res.string.speed_camera_type_fixed
        SpeedCameraType.TOWER -> Res.string.speed_camera_type_tower
        SpeedCameraType.DISCRIMINATING -> Res.string.speed_camera_type_discriminating
        SpeedCameraType.SECTION -> Res.string.speed_camera_type_section
        SpeedCameraType.RED_LIGHT -> Res.string.speed_camera_type_red_light
        SpeedCameraType.LEVEL_CROSSING -> Res.string.speed_camera_type_level_crossing
        SpeedCameraType.URBAN -> Res.string.speed_camera_type_urban
        SpeedCameraType.UNKNOWN -> Res.string.speed_camera_type_unknown
    }
